import { object, string, number, date, boolean, array, InferType } from "yup";

import { ConnectionStatus } from "@capacitor/network";

export interface IScreenProps {
  app: "iCPG" | "Plus" | "CFR";
  /** the app that is running (e.g. if iCPG content is loaded in the Plus app, then hostApp is Plus) */
  hostApp: "iCPG" | "Plus" | "CFR";
  user: null | IUser;
  rememberedUsers: IRememberedUser[];
  userUpdated: (user: null | IUser, updatePushNotifications: boolean) => void;
  trust: string;
  guidelineSummaries: IGuidelineSummary[];
  algorithmOnlyGuidelineSummaries: IGuidelineSummary[];
  algorithms: IAlgorithm[];
  annotations: IAnnotation[];
  images: { name: string; data: string }[];
  bookmarks: IBookmark[];
  recentlyViewed: IRecentlyViewed[];
  saveBookmark: (
    GLID: string,
    algorithmID: IBookmark["algorithmID"],
    clinicalNoticeID: IBookmark["clinicalNoticeID"],
    pathwayID: IBookmark["pathwayID"],
    type: IBookmark["type"]
  ) => void;
  saveRecentlyViewed: (
    GLID: string,
    algorithmID: IRecentlyViewed["algorithmID"],
    clinicalNoticeID: IRecentlyViewed["clinicalNoticeID"],
    pathwayID: IRecentlyViewed["pathwayID"],
    type: IRecentlyViewed["type"]
  ) => void;
  isGuidelineUpdated: (guidelineSummary: IGuidelineSummary) => boolean;
  updateGuidelineLastOpenedVersion: (guidelineSummary: IGuidelineSummary) => void;
  patientAge: IPatientAge;
  updatePatientAge: (patientAge: IPatientAge, from: string) => void;
  furtherResources: IFurtherResource[];
  significantChanges: ISignificantChange[];
  significantChangeAcks: ISignificantChangeAck[];
  clinicalNotices: IClinicalNotice[];
  clinicalNoticeAcks: IClinicalNoticeAck[];
  pathwayData: IPathwayData | null;
  pgdAcks: IPGDAck[];
  glAcks: IGLAck[];
  pgdTrustInfo: IPGDTrustInfo | null;
  trustPins: ITrustPin[];
  staffGroups: IStaffGroup[];
  regions: IRegion[];
  restrictedSections: IRestrictedSection[];
  helpCentreItems: IHelpCentreItem[];
  trustInfo: ITrustInfo[];
  sectionColors: ISectionColor[];
  appNotice: IAppNotice | null;
  essentialSections: IEssentialSection[];
  notifications: INotification[];
  notificationCount: number;
  trendingSearches: string[];
  notificationClicked: (notification: INotification) => void;
  markNotificationsAsRead: () => void;
  acknowledgeClinicalNotice: (clinicalNotice: IClinicalNotice) => void;
  acknowledgeSignificantChange: (significantChange: ISignificantChange, from: string) => void;
  acknowledgePGD: (guidelineSummary: IGuidelineSummary) => Promise<boolean>;
  checkPGDRemoteStatus: (guidelineSummary: IGuidelineSummary) => Promise<void>;
  acknowledgeGuideline: (guidelineSummary: IGuidelineSummary) => Promise<boolean>;
  navigateToGuideline: (
    GLID: string,
    currentTab: string,
    history: any,
    params: { [key: string]: any } | null,
    from: string
  ) => void;
  navigateToAlgorithm: (algorithmID: string, currentTab: string, history: any, from: string) => void;
  navigateToClinicalNotice: (
    clinicalNoticeID: IClinicalNotice["ID"],
    currentTab: string,
    history: any,
    params: { [key: string]: any } | null,
    from: string
  ) => void;
  navigateToPathway: (
    pathway: IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere,
    currentTab: string,
    history: any,
    from: string
  ) => void;
  navigateToEssentialSection: (
    essentialSection: IEssentialSection,
    currentTab: string,
    history: any,
    from: string
  ) => void;
  navigateToTrustPinsList: (trustPin: ITrustPin, currentTab: string, history: any) => void;
  takeParaPassPGDQuiz: (name: string) => void;
  takeParaPassQuiz: (GLID: string) => void;
  guidelineTextFinishedLoading: boolean;
  getGuidelineText: (GLID: string) => string;
  getClinicalNoticeText: (CNID: number) => string;
  getEssentialsText: (GLID: string) => string;
  showAccountModal: (shouldShow: boolean) => void;
  showAddAccountLoginModal: (shouldShow: boolean) => void;
  showHelpCentre: () => void;
  showContactPage: () => void;
  showTabBar: (shouldShow: boolean) => void;
  tabBarVisible: boolean;
  showStatusBar: (shouldShow: boolean, backgroundColor?: string) => void;
  fontSizeChange: number;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  lineHeightChange: number;
  increaseLineHeight: () => void;
  decreaseLineHeight: () => void;
  resetLineHeight: () => void;
  selectedFontFamily: string;
  setSelectedFontFamily: (fontFamily: string) => void;
  showTextToSpeech: boolean;
  setShowTextToSpeech: (shouldShow: boolean) => void;
  textToSpeechSpeed: number;
  setTextToSpeechSpeed: (value: number) => void;
  darkMode: boolean;
  appearance: "Auto" | "Dark" | "Light";
  setAppearance: (value: IScreenProps["appearance"]) => void;
  showColorFilter: boolean;
  setShowColorFilter: (shouldShow: boolean) => void;
  filterColor: { r: number; g: number; b: number; a: number | undefined };
  setFilterColor: (color: IScreenProps["filterColor"]) => void;
  freezeFirstDrugColumn: boolean;
  setFreezeFirstDrugColumn: (value: boolean) => void;
  keepScreenAwake: boolean;
  setKeepScreenAwake: (value: boolean) => void;
  keepScreenAwakeSupported: boolean;
  fontWeight: string;
  setFontWeight: (value: string) => void;
  accessabilityUnderlineLinks: boolean;
  setAccessabilityUnderlineLinks: (value: boolean) => void;
  hideParaPassLinks: boolean;
  setHideParaPassLinks: (value: boolean) => void;
  lowVisMode: boolean;
  setLowVisMode: (value: boolean) => void;
  testMode: boolean;
  debugMode: boolean;
  abTestingVersion: string;
  trustGuidelineSummariesWithAlgorithms: IGuidelineSummary[];
  showAccountSwitcher: (shouldShow: boolean) => void;
  switchAccount: (rememberedUser: IRememberedUser, trust: string) => void;
  toggleTestMode: () => void;
  toggleDebugMode: () => void;
  networkStatus: null | ConnectionStatus;
  messageBarText: string;
  loadData: () => void;
  currentSubscription: {
    valid: boolean;
    title: string;
    description: string;
    expiryDate: Date;
    expiryDisplayDate: string;
    subscriberID: string;
    appData: IAppData | null;
    app: IScreenProps["app"];
  } | null;
  oneMonthProduct: ISubscriptionProduct | null;
  twelveMonthProduct: ISubscriptionProduct | null;
  purchaseProduct: (product: ISubscriptionProduct) => void;
  restorePurchases: () => void;
  purchasingState: null | "oneMonth" | "twelveMonth" | "restoring";
  manageSubscriptions: () => void;
  sendSupportEmail: (
    type: "Subscription Support" | "Feedback" | "Delete Account" | "Search Suggestion",
    params?: any
  ) => void;
  logout: (allAccounts: boolean) => Promise<void>;
  setAppHistory: (history: any) => void;
  finishOnboarding: () => void;
  requestAccountDeletion: () => void;
  updateStaffGroups: (staffGroups: string) => void;
  updateRegions: (regions: string) => void;
  openParaPass: () => void;
  openParaFolio: () => void;
  showOlark: (shouldShow: boolean) => void;
  getGuidelineTitleForLogging: (GLID: string | null | undefined) => string;
}

export interface IUser {
  token: string;
  /** null if temporary user */
  uid: string | null;
  email: string;
  userName: string;
  subscriptions: [];
  staffGroups: string[];
  regions: string[];
  trust: string;
  availableTrusts: string[];
  pushNotificationTopics?: string[];
  isTemporary: boolean;
  isEPRS: boolean;
  isSSO?: boolean;
  temporaryLoginTime?: number;
  OTA_DEBUG?: boolean;
  // [key: string]: any // for any additional properties
}

export interface IRememberedUser {
  token: string;
  uid: string;
  email: string;
  trust: string;
  availableTrusts: string[];
  isSSO?: boolean;
}

const appFeatureSchema = object({
  description: string().required(),
  name: string().required(),
  products: array().of(string().required()).required(),
  trusts: array().of(string().required()).required(),
  testing: boolean().required(),
  live: boolean().required(),
  iCPGTesting: boolean(),
  iCPGLive: boolean(),
  plusTesting: array().of(string().required()),
  plusLive: array().of(string().required()),
  cfrTesting: array().of(string().required()),
  cfrLive: array().of(string().required()),
  lastModified: string().required(),
});

export type IAppFeature = InferType<typeof appFeatureSchema>;
export function validateIAppFeatures(items: IAppFeature[]) {
  for (let item of items) {
    let isValid = appFeatureSchema.isValidSync(item, { strict: true });
    if (!isValid) return false;
  }
  return true;
}

// export interface IAppFeature {
//   description: string,
//   name: string,
//   products: string[],
//   trusts: string[],
//   testing: boolean,
//   live: boolean,
//   lastModified: string,
// }

const appSettingSchema = object({
  iCPG: object({
    serverUrl: string().required(),
    showOlark: boolean().optional(),
    ssoTrusts: array().of(string().required()).optional(),
  }).required(),
  Plus: object({
    serverUrl: string().required(),
    showOlark: boolean().optional(),
    ssoTrusts: array().of(string()).optional(),
  }).required(),
  CFR: object({
    serverUrl: string().required(),
    showOlark: boolean().optional(),
    ssoTrusts: array().of(string()).optional(),
  }).required(),
});

export type IAppSettings = InferType<typeof appSettingSchema>;
export function validateIAppSettings(item: IAppSettings) {
  return appSettingSchema.isValidSync(item, { strict: true });
}

// export interface IAppSettings {
//   "iCPG": {
//     "serverUrl": string
//   },
//   "Plus": {
//     "serverUrl": string
//   },
//   "CFR": {
//     "serverUrl": string
//   }
// }

const furtherResourceSchema = object({
  name: string().required(),
  resourceID: number().required(),
  type: string().required(),
  trusts: array().of(string().required()).required(),
  products: array().of(string().required()).required(),
  iconUrl: string(),
  iconFileName: string(),
  link: string(),
  iosAppLink: string(),
  iosDeepLink: string(),
  androidPackage: string(),
  googlePlayLink: string(),
  GLID: string(),
  enabled: boolean(),
  lastModified: string().required(),
});

export type IFurtherResource = InferType<typeof furtherResourceSchema>;
export function validateIFurtherResources(items: IFurtherResource[]) {
  for (let item of items) {
    let isValid = furtherResourceSchema.isValidSync(item, { strict: true });
    if (!isValid) return false;
  }
  return true;
}

// export interface IFurtherResource {
//   name: string,
//   resourceID: number,
//   type: string,
//   trusts: string[],
//   products: string[],
//   iconUrl: string,
//   iconFileName: string,
//   link: string,
//   iosAppLink: string,
//   iosDeepLink: string,
//   androidPackage: string,
//   googlePlayLink: string,
//   GLID: string,
//   enabled: boolean,
//   lastModified: string,
// }

export interface IGuidelineSummary {
  GLID: string;
  GuidelineType?: string;
  images?: { imageName: string }[];
  IndexWords?: string;
  iosnodeid: string;
  publ_major_version_no: string;
  publ_minor_version_no: string;
  Section: string;
  SubTitle: string;
  Title: string;
  SG?: string;
  updated?: boolean;
  remove?: number;
  PGDType?: "Auth" | "AuthWithQuiz" | "NoAuth";
  SupportAcknowledment?: boolean;
  AcknowledgedTime?: string;
  /** added property when loading - not part of json */
  trust?: string;
  /** added property when loading - not part of json */
  annotations?: IAnnotation[];
  /** added property when loading - not part of json */
  informationOnly?: boolean;
}

export interface IGuideline {
  GLID: string;
  publ_guideline_type: string;
  publ_major_version_no: string;
  publ_minor_version_no: string;
  publ_section_title?: string;
  "cpg-guideline-subtitle"?: string;
  Section: string;
  title: string;
  remove?: boolean;
  sections?: any[];
  html?: string;
  /** added property when loading - not part of json */
  trust?: string;
  UpdatedDisplay?: string;
}

export interface ILastOpenedGuidelineVersions {
  [GLID: string]: {
    publ_major_version_no: string;
    publ_minor_version_no: string;
    updated: boolean;
  };
}

export interface IAlgorithm {
  caption: string;
  GLID?: string;
  id: string;
  image?: string;
  inc: string;
  name: string;
  Section: string;
  GuideLineSectionTitle?: string;
  updated?: string;
  annotations?: IAnnotation[];
}

export interface IAnnotation {
  ID: string;
  Reference: string;
  "note (html)"?: string;
  ShowWaterMark?: string;
  /** updated prop is only on first algorithm downloaded to decide if algorithms need downloading */
  updated?: string;
  "Hide JRCALC?"?: string;
  Age?: string;
  Route?: string;
  Images?: any[];
}

export interface IBookmark {
  GLID: string;
  algorithmID: string | null;
  clinicalNoticeID?: number | null;
  pathwayID?: string | null;
  type: "guideline" | "algorithm" | "clinical notice" | "pathway" | "essentials";
  time: number;
}

export interface IRecentlyViewed {
  GLID: string;
  algorithmID: string | null;
  clinicalNoticeID?: number | null;
  pathwayID?: string | null;
  type: "guideline" | "algorithm" | "clinical notice" | "pathway" | "essentials";
  time: number;
}

export interface IPatientAge {
  name: string;
  age: string;
  months: number;
  PFA: string;
  range: string;
}

export interface ISignificantChange {
  title: string;
  GLID: string;
  id: string;
  description: string;
  trust?: string;
  updated: {
    date: string;
    string: string;
    dateObject: string;
  };
}

export interface ISignificantChangeAck {
  cnid: string;
  contactID: string;
  title: string;
  timestamp: string;
  trust: string;
}

export interface IClinicalNotice {
  // id: string // this property does exist but it's the airtable ID which shouldn't be used
  /** clinical notice ID - not Airtable */
  ID: number;
  Title: string;
  "Include in CFR"?: boolean;
  "Include in PLUS"?: boolean;
  status: string;
  updated: string;
  released?: string;
  Keywords?: string;
  content_container: string;
  regions?: string;
  "pgd permissions"?: string;
  LastModifiedTime: string;
  informationOnly?: boolean;
  localClinicalUpdate?: boolean;
}

export interface IClinicalNoticeAck {
  cnid: number;
  contactID: string;
  title: string;
  timestamp: string;
  trust: string;
}

export interface IPGDAck {
  GLID: string;
  versionNumber: string;
  ACKTime: string | null;
  ViewTime: string | null;
  QuizAttemptTime: string | null;
  PreviousAckTimes?: string | null;
}

export interface IGLAck {
  GLID: string;
  contactID: string;
  title: string;
  timestamp: string;
  trust: string;
}

export interface IAppData {
  updated: number;
  bookmarks?: IBookmark[];
  recentlyViewed?: IRecentlyViewed[];
  pushNotificationTopics?: string[];
}

export interface IRemoteDataRequest {
  type: string;
  url: string;
  parameters: { [key: string]: any };
  httpMethod: "GET" | "POST";
}

export interface ILocalUsageLogItem {
  userID: string;
  id: string;
  title: string;
  timestamp: string;
  type: "guideline" | "algorithm" | "clinicalNoticeAck" | "significantChangeAck";
}

export interface IPGDTrustInfo {
  trust: string;
  hasPGDs: "Yes" | "No";
  hasQuizzes: "Yes" | "No";
  authorisationType: "Auto" | "Manual" | "No Authorisation or Dashboard";
  authorisationText: string;
  pmaAuthorisationText?: string;
  smgAuthorisationText?: string;
  pmaDuration?: number;
}

export interface ITrustPin {
  name: string;
  type: string;
  guidelines: string[];
  guidelinesInSections?: string[];
  algorithms: string[];
  algorithmsInSections?: string[];
  clinicalNotices: string[];
  clinicalNoticesInSections?: string[];
  referenceCards?: IReferenceCard[];
  displayAsReferenceCard?: boolean;
  displayAsDashboardCard?: boolean;
  furtherResourceID: number[];
  iconUrl: string;
  iconFileName: string;
  airtableIconUrl: string;
  iconDarkUrl: string;
  iconDarkFileName: string;
  airtableIconDarkUrl: string;
  products?: string[];
  enabled: boolean;
  lastModified: string;
}

export interface IReferenceCard {
  GLID: string;
  imageName?: string;
  imageNames?: string[];
  dontDisplayImage?: boolean;
}

export interface IStaffGroup {
  displayName: string;
  staffGroupName: string;
  lastModified: string;
}

export interface IRegion {
  name: string;
  lastModified: string;
}

export interface IRestrictedSection {
  name: string;
  SG: string[];
}

export interface IDashboardCardState {
  name: string;
  collapsed: boolean;
  reorderable: boolean;
  removable: boolean;
  removed: boolean;
  position: number;
}

export interface ISearchResult {
  type: "guideline" | "algorithm" | "clinical notice" | "essentials" | "further resource" | "pathway";
  guidelineSummary: IGuidelineSummary | null;
  algorithm: IAlgorithm | null;
  clinicalNotice: IClinicalNotice | null;
  pathway?: IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere | null;
  essentialSection?: IEssentialSection | null;
  furtherResource?: IFurtherResource | null;
  titleMatch: boolean;
  matches: number;
  score: number;
  sample: null | string;
}

export interface IRecentSearch {
  GLID?: string;
  algorithmID?: string;
  clinicalNoticeID?: number;
  pathwayID?: string;
  essentialSectionID?: string;
  furtherResourceID?: number;
  type: ISearchResult["type"];
}

export type IListType =
  | "Guidelines"
  | "Meds"
  | "Algorithms"
  | "PageForAge"
  | "Bookmarks"
  | "RecentlyViewed"
  | "ClinicalNotices"
  | "LocalClinicalUpdates"
  | "Covid19"
  | "TimeCritical"
  | "PGDs"
  | "PMAs"
  | "SMGs"
  | "TrustPin"
  | "TrustGuidelines"
  | "Essentials"
  | "AcknowledgeableGuidelines"
  | "SharedProcedures";

export interface IListFilterItem {
  title: string;
  value: string;
  group: string;
  class: string | null;
  indentation?: number;
}

// export interface IPathwayEntry {
//   id: string
//   type: "pathway" | "bypassNumber" | "whatGoesWhere",
//   pathway?: IPathway | null,
//   bypassNumber?: IPathwayBypassNumber | null,
//   whatGoesWhere? : IPathwayWhatGoesWhere | null,
// }

export interface IPathwayData {
  Bypassnumbers: IPathwayBypassNumber[];
  categories: IPathwayCategory[];
  LocationDetails: IPathwayLocationDetail[];
  Pathways: IPathway[];
  Places: IPathwayPlace[];
  CouncilAreas?: IPathwayCouncilArea[];
  Whatgoeswhere: IPathwayWhatGoesWhere[];
  updated: string;
}

export interface IPathway {
  id: string;
  areas?: string[];
  categories?: string[];
  fields: {
    Area?: string[];
    "Health Board"?: string[];
    "Inclusion criteria"?: string;
    "Exclusion criteria"?: string;
    "Referral Process"?: string;
    Notes?: string;
    "Last modified time"?: string;
    "Location details"?: string[];
    "Pathway ID"?: string;
    "Pathway name"?: string;
    "Pathway Categories"?: string[];
    // Pathway?: string,
    GroupLocationAreas?: boolean;
    AuthorisedUsers?: string;
    VersionNumber?: string;
    PDF?: { filename: string }[];
    "Search Terms"?: string[];
    "User regions"?: string[];
    PathwayImage?: [
      {
        id: string;
        width: number;
        height: number;
        url: string;
        filename: string;
        size: number;
        type: string;
      }
    ];
    "Major changes"?: string;
    "Only visible to"?: string[];
  };
  /** added property when loading - not part of json */
  searchScore?: number;
}

export interface IPathwayBypassNumber {
  id: string;
  fields: {
    "Last modified time": string;
    "Location details": string[];
    "Pathway category": string;
    "Major changes"?: string;
    "Search Terms"?: string[];
  };
  /** added property when loading - not part of json */
  searchScore?: number;
}

export interface IPathwayCategory {
  id: string;
  fields: {
    Category: string;
    "Last modified time": string;
    "Pathway Type": string;
  };
}

export interface IPathwayLocationDetail {
  id: string;
  fields: {
    Area?: string[];
    "Bypass numbers"?: string[];
    Contact?: string;
    "Last modified time": string;
    Location?: string[];
    "Council Areas"?: string[];
    "Health Board"?: string[];
    Pathways?: string[];
    "Opening Times display"?: string;
    "Phone Number"?: string;
    "Phone Number 2"?: string;
    "Phone Number 3"?: string;
    Email?: string;
  };
}

export interface IPathwayPlace {
  id: string;
  fields: {
    Abbreviation: string;
    Title: string;
    "Town/City": string;
    "Linked Contact": string[];
    Type: string[];
    Address: string;
    PostcodeTown: string;
    Notes: string;
    Name: string;
    "Last modified time": string;
  };
}

export interface IPathwayCouncilArea {
  id: string;
  fields: {
    Name?: string;
    "Health Board?": string;
  };
}

export interface IPathwayWhatGoesWhere {
  id: string;
  fields: {
    "1"?: string;
    "2"?: string;
    "3"?: string;
    "4"?: string;
    Name?: string;
    Pathway?: string;
    "1 location details"?: string[];
    "2 location details"?: string[];
    "3 location details"?: string[];
    "4 location details"?: string[];
    "Last modified time": string;
    PathwayImage?: [
      {
        id: string;
        width: number;
        height: number;
        url: string;
        filename: string;
        size: number;
        type: string;
      }
    ];
    "Major changes"?: string;
    "Search Terms"?: string[];
  };
  /** added property when loading - not part of json */
  searchScore?: number;
}

export interface INotification {
  id: number;
  title?: string;
  contents?: string;
  trust?: string | null;
  topics?: string[];
  data?: object;
  date?: number;
  isSent?: boolean;
}

export interface IEssentialSection {
  Chapter: string;
  Section: string;
  HtmlData: string;
  SubSection: string;
  Name: string;
  id: string;
}

export interface IHelpCentreItem {
  id: string;
  title: string;
  html: string;
  section: string;
  airtableImageUrl: string;
  imageUrl: string;
  imageFileName: string;
  classUrl: string;
  videoID: string;
  app: string[];
  trusts?: string[];
  enabled: boolean;
}

export interface ITrustInfo {
  id: string;
  trust: string;
  html: string;
  pathwayContactEmail?: string;
}

export interface IAppNotice {
  ID: number;
  title: string;
  content: string;
  actionButtonText?: string;
  link?: string;
  products: string[];
  trusts: string[];
}

export interface ISectionColor {
  name: string;
  color: string;
  trusts: string[];
}

export interface IAbbreviation {
  Title: string;
  Text: string;
  ID: string;
}

export interface ICFRbyTrustItem {
  id: string;
  Name: string;
  Notes: string;
  [key: string]: string | boolean;
}

export interface IAppEntitlementsResponse {
  success: boolean;
  subs: ISubscriptionPurchase[];
}

export interface ISubscriptionProduct {
  productId: string;
  price: string;
  displayName: string;
  description: string;
}

export interface ISubscriptionPurchase {
  id?: string;
  productId?: string;
  transactionId?: string;
  startDate?: string;
  endDate?: string;
}
